import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useCompany } from "../../../contexts/CompanyContext";
import { arTasks, arCollabTasks, concertTasks, concertCollabTasks, foundationTasks, foundationCollabTasks, ipTasks, ipCollabTasks } from "../../../mocks/hubTasks";
import { campaigns } from "../../../mocks/campaigns";
import { useNavigate } from "react-router-dom";

interface HubStat {
  hub: string;
  label: string;
  icon: string;
  color: string;
  total: number;
  done: number;
  path: string;
}

const ALL_HUB_TASKS = {
  ar: [...arTasks, ...arCollabTasks],
  concert: [...concertTasks, ...concertCollabTasks],
  foundation: [...foundationTasks, ...foundationCollabTasks],
  ip: [...ipTasks, ...ipCollabTasks],
  campaign: campaigns.flatMap(c => c.tasks),
};

const HUB_META: Omit<HubStat, "total" | "done">[] = [
  { hub: "ar", label: "A&R 허브", icon: "ri-music-2-line", color: "text-[#7A9BFF]", path: "/ar-hub" },
  { hub: "concert", label: "공연기획 허브", icon: "ri-building-line", color: "text-violet-400/80", path: "/concert-hub" },
  { hub: "foundation", label: "재단·사업관리", icon: "ri-community-line", color: "text-amber-400/80", path: "/foundation-hub" },
  { hub: "ip", label: "IP 스튜디오", icon: "ri-copyright-line", color: "text-emerald-400/80", path: "/ip-hub" },
  { hub: "campaign", label: "캠페인 허브", icon: "ri-advertisement-line", color: "text-pink-400/70", path: "/campaign-hub" },
];

export default function HubProgressPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaceId } = useCompany();
  const [completedMap, setCompletedMap] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);

  const fetchCompletions = useCallback(async () => {
    if (!workspaceId || !user?.id) { setLoading(false); return; }
    const { data } = await supabase
      .from("task_completions")
      .select("hub, task_id")
      .eq("workspace_id", workspaceId)
      .eq("user_id", user.id);
    const map: Record<string, Set<string>> = {};
    (data ?? []).forEach(row => {
      if (!map[row.hub]) map[row.hub] = new Set();
      map[row.hub].add(row.task_id);
    });
    setCompletedMap(map);
    setLoading(false);
  }, [workspaceId, user?.id]);

  useEffect(() => {
    fetchCompletions();
  }, [fetchCompletions]);

  // ── 실시간 task_completions 구독 ──────────────────────
  useEffect(() => {
    if (!workspaceId || !user?.id) return;
    const channel = supabase
      .channel(`hub-progress-${workspaceId}-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "task_completions",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        () => { fetchCompletions(); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [workspaceId, user?.id, fetchCompletions]);

  const stats: HubStat[] = HUB_META.map(meta => {
    const allTasks = ALL_HUB_TASKS[meta.hub as keyof typeof ALL_HUB_TASKS] ?? [];
    const done = completedMap[meta.hub]?.size ?? 0;
    return { ...meta, total: allTasks.length, done };
  });

  const totalTasks = stats.reduce((s, h) => s + h.total, 0);
  const totalDone = stats.reduce((s, h) => s + h.done, 0);
  const overallPct = totalTasks > 0 ? Math.round((totalDone / totalTasks) * 100) : 0;

  return (
    <div className="bg-[#111112] border border-white/[0.05] rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[#E0E0E0] text-sm font-semibold">허브 태스크 진행률</h3>
          <p className="text-[#3D3D3D] text-[11px] mt-0.5">내 직무 허브 전체 달성 현황</p>
        </div>
        <div className="text-right">
          <div className="text-[#4B6CF7] text-lg font-bold tabular-nums">{overallPct}%</div>
          <div className="text-[#3D3D3D] text-[10px]">{totalDone}/{totalTasks} 완료</div>
        </div>
      </div>

      {/* Overall bar */}
      <div className="w-full bg-white/[0.04] rounded-full h-1.5 mb-5">
        <div
          className="h-1.5 rounded-full bg-gradient-to-r from-[#4B6CF7]/70 to-[#7A9BFF]/70 transition-all duration-700"
          style={{ width: `${overallPct}%` }}
        />
      </div>

      {/* Per-hub bars */}
      <div className="space-y-3">
        {stats.map(stat => {
          const pct = stat.total > 0 ? Math.round((stat.done / stat.total) * 100) : 0;
          return (
            <button
              key={stat.hub}
              type="button"
              onClick={() => navigate(stat.path)}
              className="w-full text-left group cursor-pointer"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${stat.icon} text-xs ${stat.color}`}></i>
                  </div>
                  <span className={`text-[11px] font-medium transition-colors ${pct === 100 ? "text-[#555555]" : "text-[#999999] group-hover:text-[#BBBBBB]"}`}>
                    {stat.label}
                  </span>
                  {loading && <span className="text-[#333333] text-[10px]">로딩중...</span>}
                </div>
                <div className="flex items-center gap-2">
                  {pct === 100 && (
                    <span className="text-[9px] text-emerald-400/70 bg-emerald-400/10 border border-emerald-400/20 px-1.5 py-0.5 rounded-full">완료</span>
                  )}
                  <span className="text-[#3D3D3D] text-[11px] tabular-nums">{stat.done}/{stat.total}</span>
                </div>
              </div>
              <div className="w-full bg-white/[0.04] rounded-full h-1">
                <div
                  className={`h-1 rounded-full transition-all duration-500 ${pct === 100 ? "bg-emerald-400/40" : "bg-[#4B6CF7]/50"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Collab hint */}
      <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-emerald-400/[0.04] border border-emerald-400/10 rounded-lg">
        <i className="ri-team-line text-emerald-400/50 text-xs shrink-0"></i>
        <p className="text-emerald-400/50 text-[10px] leading-relaxed">
          초록 점(<span className="text-emerald-400/70">●</span>) 태스크는 다른 직무와 협업이 필요한 태스크입니다
        </p>
      </div>
    </div>
  );
}
